/**
 * QuickReplacePopover - Fast media replacement with AI-assisted suggestions
 *
 * Shows a popover with 4 thumbnail alternatives when user clicks Replace.
 * Uses search terms from template HTML (data-search-terms) to fetch relevant suggestions.
 * Provides quick one-click replacement or option to open full search modal.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { RefreshCcw, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { mediaService } from '../../services/mediaService';
import { ElementSuggestionMetadata, QuickReplaceSuggestion } from '../../types/elementSuggestion';

interface QuickReplacePopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  suggestionMetadata: ElementSuggestionMetadata | null;
  onQuickReplace: (url: string, source: string) => void;
  onOpenFullSearch: () => void;
  children: React.ReactNode;
}

export const QuickReplacePopover: React.FC<QuickReplacePopoverProps> = ({
  isOpen,
  onOpenChange,
  suggestionMetadata,
  onQuickReplace,
  onOpenFullSearch,
  children,
}) => {
  const [suggestions, setSuggestions] = useState<QuickReplaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');

  /**
   * Fetch suggestions from media service using the provided search term
   */
  const fetchSuggestions = useCallback(async (searchTerm?: string) => {
    // Use provided term or first from metadata
    const term = searchTerm || suggestionMetadata?.searchTerms?.[0];
    if (!term) {
      console.log('[QuickReplace] No search term available');
      return;
    }

    setLoading(true);
    setCurrentSearchTerm(term);

    try {
      console.log('[QuickReplace] Fetching suggestions for:', term);

      // Determine if we should search videos or images based on asset type
      const assetType = suggestionMetadata?.assetType;

      if (assetType === 'video') {
        const response = await mediaService.searchVideos(term, 1, 4);
        const mapped: QuickReplaceSuggestion[] = (response.videos || []).slice(0, 4).map((video: any) => ({
          id: video.id,
          thumbnail: video.image || video.video_files?.[0]?.link,
          fullUrl: video.video_files?.find((f: any) => f.quality === 'hd')?.link || video.video_files?.[0]?.link,
          source: video.source || 'pexels',
          photographer: video.user?.name,
          width: video.width,
          height: video.height,
        }));
        setSuggestions(mapped);
      } else {
        // Default to images
        const response = await mediaService.searchPhotos(term, 1, 4);
        const mapped: QuickReplaceSuggestion[] = (response.photos || []).slice(0, 4).map((photo: any) => ({
          id: photo.id,
          thumbnail: photo.src?.small || photo.src?.medium,
          fullUrl: photo.src?.large2x || photo.src?.large || photo.src?.original,
          source: photo.source || 'pexels',
          photographer: photo.photographer,
          width: photo.width,
          height: photo.height,
        }));
        setSuggestions(mapped);
      }
    } catch (error) {
      console.error('[QuickReplace] Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [suggestionMetadata]);

  // Fetch suggestions when popover opens
  useEffect(() => {
    if (isOpen && suggestionMetadata?.searchTerms?.length) {
      fetchSuggestions();
    }
  }, [isOpen, suggestionMetadata, fetchSuggestions]);

  // Handle refresh with a different search term
  const handleRefresh = () => {
    if (!suggestionMetadata?.searchTerms?.length) return;

    // Cycle through search terms
    const currentIndex = suggestionMetadata.searchTerms.indexOf(currentSearchTerm);
    const nextIndex = (currentIndex + 1) % suggestionMetadata.searchTerms.length;
    const nextTerm = suggestionMetadata.searchTerms[nextIndex];

    fetchSuggestions(nextTerm);
  };

  // Handle clicking on a suggestion thumbnail
  const handleSuggestionClick = (suggestion: QuickReplaceSuggestion) => {
    console.log('[QuickReplace] Selected:', suggestion);
    onQuickReplace(suggestion.fullUrl, suggestion.source);
    onOpenChange(false);
  };

  // If no metadata is available, don't show the popover - just render children directly
  if (!suggestionMetadata?.searchTerms?.length) {
    return <>{children}</>;
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children as any}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        {/* Header with search term badges */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs text-muted-foreground font-medium">Suggestions:</span>
          {suggestionMetadata.searchTerms.slice(0, 2).map((term, i) => (
            <Badge
              key={i}
              variant="secondary"
              className={cn(
                "text-[10px] cursor-pointer transition-colors",
                term === currentSearchTerm && "bg-primary text-primary-foreground"
              )}
              onClick={() => fetchSuggestions(term)}
            >
              {term}
            </Badge>
          ))}
          {suggestionMetadata.searchTerms.length > 2 && (
            <span className="text-[10px] text-muted-foreground">
              +{suggestionMetadata.searchTerms.length - 2} more
            </span>
          )}
        </div>

        {/* Semantic context hint if available */}
        {suggestionMetadata.semanticContext && (
          <p className="text-[10px] text-muted-foreground mb-3 line-clamp-1 italic">
            {suggestionMetadata.semanticContext}
          </p>
        )}

        {/* 2x2 thumbnail grid */}
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {loading ? (
            // Loading skeletons
            [...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-square bg-muted animate-pulse rounded"
              />
            ))
          ) : suggestions.length > 0 ? (
            // Suggestion thumbnails
            suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="aspect-square rounded overflow-hidden ring-1 ring-border hover:ring-2 hover:ring-primary transition-all group"
                title={`${suggestion.photographer || 'Unknown'} (${suggestion.source})`}
              >
                <img
                  src={suggestion.thumbnail}
                  alt={`Suggestion from ${suggestion.source}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                />
              </button>
            ))
          ) : (
            // No results
            <div className="col-span-4 text-center py-4 text-xs text-muted-foreground">
              No suggestions found. Try a different term or open full search.
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex justify-between items-center pt-2 border-t border-border/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-7 px-2 text-xs"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <RefreshCcw className="w-3 h-3 mr-1" />
            )}
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onOpenChange(false);
              onOpenFullSearch();
            }}
            className="h-7 px-3 text-xs"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            More Options
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
